import React, { useState, useEffect, useCallback } from "react";
import api from "../../services/api";
import PasswordInput from "../../components/PasswordInput";

const ROLES = ["user", "subadmin", "admin"];

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatDatetime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isExpired(expiresAt) {
  return expiresAt && new Date() > new Date(expiresAt);
}

export default function UsersTab({ currentUser }) {
  const isAdmin = currentUser?.role === "admin";
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);

  const emptyForm = {
    username: "",
    email: "",
    fullName: "",
    phoneNumber: "",
    password: "",
    role: "user",
    maxProjects: 1,
    unrestrictedTargets: false,
    temporaryEnabled: false,
    temporaryExpiresAt: "",
  };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.adminGetUsers();
      setUsers(res.data.users);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingUser(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setForm({
      username: user.username,
      email: user.email,
      fullName: user.fullName || "",
      phoneNumber: user.phoneNumber || "",
      password: "",
      role: user.role,
      maxProjects: user.maxProjects ?? 1,
      unrestrictedTargets: !!user.unrestrictedTargets,
      temporaryEnabled: !!(
        user.temporaryAccess?.enabled && user.temporaryAccess?.expiresAt
      ),
      temporaryExpiresAt: user.temporaryAccess?.expiresAt
        ? new Date(user.temporaryAccess.expiresAt).toISOString().slice(0, 10)
        : "",
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.temporaryEnabled && !form.temporaryExpiresAt) {
      setError("Please set an expiry date for temporary access");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        username: form.username,
        email: form.email,
        fullName: form.fullName,
        phoneNumber: form.phoneNumber,
        role: form.role,
        ...(isAdmin
          ? {
              maxProjects: Math.max(1, Number(form.maxProjects) || 1),
              unrestrictedTargets: !!form.unrestrictedTargets,
            }
          : {}),
        temporaryAccess: {
          enabled: form.temporaryEnabled,
          expiresAt:
            form.temporaryEnabled && form.temporaryExpiresAt
              ? form.temporaryExpiresAt
              : null,
        },
      };
      if (form.password) payload.password = form.password;

      if (editingUser) {
        await api.adminUpdateUser(editingUser._id, payload);
      } else {
        await api.adminCreateUser(payload);
      }
      setShowForm(false);
      load();
    } catch (e) {
      setError(e.response?.data?.error || "Failed to save user");
    } finally {
      setSaving(false);
    }
  };

  const roleColor = (role) => {
    if (role === "admin")
      return {
        color: "#f59e0b",
        bg: "rgba(245,158,11,0.1)",
        border: "rgba(245,158,11,0.3)",
      };
    if (role === "subadmin")
      return {
        color: "#a78bfa",
        bg: "rgba(167,139,250,0.1)",
        border: "rgba(167,139,250,0.3)",
      };
    return {
      color: "var(--text-muted)",
      bg: "rgba(255,255,255,0.05)",
      border: "var(--border)",
    };
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h2
          style={{
            fontSize: "16px",
            fontWeight: "700",
            color: "var(--text-primary)",
            margin: 0,
          }}
        >
          Users{" "}
          {users.length > 0 && (
            <span
              style={{
                color: "var(--text-muted)",
                fontWeight: "500",
                fontSize: "13px",
              }}
            >
              ({users.length})
            </span>
          )}
        </h2>
        <button
          className="btn"
          onClick={openCreate}
          style={{ fontSize: "13px", padding: "7px 16px" }}
        >
          + Create User
        </button>
      </div>

      {error && (
        <div
          style={{
            padding: "10px 14px",
            background: "rgba(239,68,68,0.1)",
            border: "1px solid rgba(239,68,68,0.25)",
            borderRadius: "8px",
            color: "#f87171",
            fontSize: "13px",
            marginBottom: "16px",
          }}
        >
          {error}
          <button
            onClick={() => setError("")}
            style={{
              float: "right",
              background: "none",
              border: "none",
              color: "#f87171",
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* User Form Modal */}
      {showForm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: "16px",
              padding: "28px",
              width: "100%",
              maxWidth: "480px",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "700" }}>
                {editingUser ? "Edit User" : "Create User"}
              </h3>
              <button
                onClick={() => setShowForm(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--text-muted)",
                  cursor: "pointer",
                  fontSize: "20px",
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "14px",
                }}
              >
                <FormField
                  label="Full Name"
                  value={form.fullName}
                  onChange={(v) => setForm((f) => ({ ...f, fullName: v }))}
                  placeholder="Full Name"
                />
                <FormField
                  label="Username *"
                  value={form.username}
                  onChange={(v) => setForm((f) => ({ ...f, username: v }))}
                  placeholder="username"
                  required
                  disabled={!!editingUser && !isAdmin}
                />
                <FormField
                  label="Email *"
                  value={form.email}
                  onChange={(v) => setForm((f) => ({ ...f, email: v }))}
                  placeholder="email@example.com"
                  type="email"
                  required
                  disabled={!!editingUser && !isAdmin}
                />
                <FormField
                  label="Phone Number"
                  value={form.phoneNumber}
                  onChange={(v) => setForm((f) => ({ ...f, phoneNumber: v }))}
                  placeholder="+1 555 000 0000"
                />
                <FormField
                  label={
                    editingUser
                      ? "New Password (leave blank to keep)"
                      : "Password *"
                  }
                  value={form.password}
                  onChange={(v) => setForm((f) => ({ ...f, password: v }))}
                  placeholder="••••••••"
                  type="password"
                  required={!editingUser}
                />

                {isAdmin && (
                  <div>
                    <label style={labelStyle}>
                      Projects Allowed
                      {editingUser
                        ? ` (currently using ${editingUser.projectCount ?? 0})`
                        : ""}
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={form.maxProjects}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, maxProjects: e.target.value }))
                      }
                      style={inputStyle}
                    />
                    <div
                      style={{
                        fontSize: "11px",
                        color: "var(--text-muted)",
                        marginTop: "4px",
                        lineHeight: "1.5",
                      }}
                    >
                      How many projects this user can create. Each project locks
                      one main domain plus up to two competitors. Lowering this
                      below their current count does not delete anything — it
                      only stops them creating more.
                    </div>
                  </div>
                )}

                {isAdmin && (
                  <div>
                    <label
                      style={{
                        ...labelStyle,
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!form.unrestrictedTargets}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            unrestrictedTargets: e.target.checked,
                          }))
                        }
                        style={{ width: "14px", height: "14px", cursor: "pointer" }}
                      />
                      Internal team — free URL entry
                    </label>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "var(--text-muted)",
                        marginTop: "4px",
                        lineHeight: "1.5",
                      }}
                    >
                      Lets this user type any URL into an agent instead of
                      picking from a project's domains, and run agents without
                      selecting a project at all. For staff auditing many
                      unrelated sites. Their existing projects and past runs
                      stay exactly as they are, still grouped by project.
                    </div>
                  </div>
                )}

                {isAdmin && (
                  <div>
                    <label style={labelStyle}>Role</label>
                    <select
                      value={form.role}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, role: e.target.value }))
                      }
                      style={{ ...inputStyle, colorScheme: "dark" }}
                    >
                      {ROLES.map((r) => (
                        <option
                          key={r}
                          value={r}
                          style={{ background: "#1e1e2e", color: "#e2e8f0" }}
                        >
                          {r}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div
                  style={{
                    borderTop: "1px solid var(--border)",
                    paddingTop: "14px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      marginBottom: "10px",
                    }}
                  >
                    <input
                      type="checkbox"
                      id="tempAccess"
                      checked={form.temporaryEnabled}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        const defaultDate = enabled
                          ? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                              .toISOString()
                              .slice(0, 10)
                          : "";
                        setForm((f) => ({
                          ...f,
                          temporaryEnabled: enabled,
                          temporaryExpiresAt: enabled
                            ? f.temporaryExpiresAt || defaultDate
                            : "",
                        }));
                      }}
                      style={{
                        width: "15px",
                        height: "15px",
                        cursor: "pointer",
                      }}
                    />
                    <label
                      htmlFor="tempAccess"
                      style={{
                        fontSize: "13px",
                        color: "var(--text-secondary)",
                        cursor: "pointer",
                      }}
                    >
                      Enable temporary access
                    </label>
                  </div>
                  {form.temporaryEnabled && (
                    <div>
                      <label style={labelStyle}>
                        {editingUser &&
                        form.temporaryExpiresAt &&
                        new Date(form.temporaryExpiresAt) < new Date()
                          ? "Extend Access Until (currently expired)"
                          : "Access Expires On"}
                      </label>
                      <input
                        type="date"
                        value={form.temporaryExpiresAt}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            temporaryExpiresAt: e.target.value,
                          }))
                        }
                        min={new Date().toISOString().slice(0, 10)}
                        style={{
                          ...inputStyle,
                          borderColor:
                            editingUser &&
                            form.temporaryExpiresAt &&
                            new Date(form.temporaryExpiresAt) < new Date()
                              ? "rgba(239,68,68,0.4)"
                              : undefined,
                        }}
                      />
                      {editingUser &&
                        form.temporaryExpiresAt &&
                        new Date(form.temporaryExpiresAt) < new Date() && (
                          <p
                            style={{
                              fontSize: "11px",
                              color: "#f87171",
                              margin: "4px 0 0",
                            }}
                          >
                            Pick a new future date to restore this user's
                            access.
                          </p>
                        )}
                    </div>
                  )}
                </div>

                {error && (
                  <div style={{ color: "#f87171", fontSize: "12px" }}>
                    {error}
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    justifyContent: "flex-end",
                    paddingTop: "4px",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="btn"
                    style={{
                      background: "transparent",
                      border: "1px solid var(--border)",
                      color: "var(--text-muted)",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn"
                    disabled={saving}
                    style={{ minWidth: "80px" }}
                  >
                    {saving ? "Saving..." : editingUser ? "Update" : "Create"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "var(--text-muted)",
          }}
        >
          Loading...
        </div>
      ) : users.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "var(--text-muted)",
            fontSize: "13px",
          }}
        >
          No users found
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {users.map((user) => {
            const rc = roleColor(user.role);
            const expired =
              isExpired(user.temporaryAccess?.expiresAt) &&
              user.temporaryAccess?.enabled;
            return (
              <div
                key={user._id}
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  borderRadius: "10px",
                  padding: "14px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    width: "34px",
                    height: "34px",
                    borderRadius: "50%",
                    background: "var(--gradient-btn)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "13px",
                    fontWeight: "700",
                    color: "white",
                    flexShrink: 0,
                  }}
                >
                  {(user.fullName || user.username).charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: "140px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "var(--text-primary)",
                      }}
                    >
                      {user.fullName
                        ? `${user.fullName} (${user.username})`
                        : user.username}
                    </span>
                    <span
                      style={{
                        fontSize: "10px",
                        fontWeight: "700",
                        padding: "2px 7px",
                        borderRadius: "5px",
                        background: rc.bg,
                        border: `1px solid ${rc.border}`,
                        color: rc.color,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      {user.role}
                    </span>
                    {user.temporaryAccess?.enabled && (
                      <span
                        style={{
                          fontSize: "10px",
                          fontWeight: "700",
                          padding: "2px 7px",
                          borderRadius: "5px",
                          background: expired
                            ? "rgba(239,68,68,0.1)"
                            : "rgba(6,182,212,0.1)",
                          border: `1px solid ${expired ? "rgba(239,68,68,0.3)" : "rgba(6,182,212,0.3)"}`,
                          color: expired ? "#f87171" : "var(--teal-bright)",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                        }}
                      >
                        {expired
                          ? "Expired"
                          : `Temp · ${formatDate(user.temporaryAccess.expiresAt)}`}
                      </span>
                    )}
                    {/* Project usage against the admin-set cap */}
                    <span
                      style={{
                        fontSize: "10px",
                        fontWeight: "700",
                        padding: "2px 7px",
                        borderRadius: "5px",
                        background:
                          (user.projectCount ?? 0) >= (user.maxProjects ?? 1)
                            ? "rgba(245,158,11,0.1)"
                            : "rgba(255,255,255,0.05)",
                        border: `1px solid ${
                          (user.projectCount ?? 0) >= (user.maxProjects ?? 1)
                            ? "rgba(245,158,11,0.3)"
                            : "var(--border)"
                        }`,
                        color:
                          (user.projectCount ?? 0) >= (user.maxProjects ?? 1)
                            ? "#f59e0b"
                            : "var(--text-muted)",
                        letterSpacing: "0.04em",
                      }}
                      title="Projects used / allowed"
                    >
                      {user.projectCount ?? 0}/{user.maxProjects ?? 1} projects
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "var(--text-muted)",
                      marginTop: "2px",
                    }}
                  >
                    {user.email}
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "var(--text-muted)",
                      marginTop: "1px",
                    }}
                  >
                    Joined {formatDate(user.createdAt)}
                    {user.createdBy &&
                      ` · Created by ${user.createdBy.username}`}
                  </div>
                </div>
                <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                  <button
                    className="btn btn-small"
                    onClick={() => openEdit(user)}
                    style={{
                      fontSize: "12px",
                      padding: "5px 12px",
                      background: "rgba(255,255,255,0.06)",
                      border: "1px solid var(--border)",
                      color: "var(--text-secondary)",
                    }}
                  >
                    Edit
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const labelStyle = {
  display: "block",
  fontSize: "12px",
  color: "var(--text-muted)",
  marginBottom: "5px",
  fontWeight: "500",
};
const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "8px 12px",
  background: "rgba(255,255,255,0.05)",
  border: "1px solid var(--border)",
  borderRadius: "8px",
  color: "var(--text-primary)",
  fontSize: "13px",
  outline: "none",
};

function FormField({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  required,
  disabled,
}) {
  const inputProps = {
    value,
    onChange: (e) => onChange(e.target.value),
    placeholder,
    required,
    disabled,
    style: {
      ...inputStyle,
      opacity: disabled ? 0.5 : 1,
      cursor: disabled ? "not-allowed" : "text",
    },
    autoComplete: type === "password" ? "new-password" : undefined,
  };

  return (
    <div>
      <label style={labelStyle}>{label}</label>
      {type === "password" ? (
        <PasswordInput {...inputProps} />
      ) : (
        <input type={type} {...inputProps} />
      )}
    </div>
  );
}
